import { Button, Card, Descriptions, Space, Tag } from 'antd'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import type { ReturnRoot } from '@repo/sdk'

import { ProductCell } from '../../components/ProductCell'
import { formatKrw } from '@repo/shared'
import { formatOrderBriefLabel } from '../orders/orderDisplay'
import { formatReturnRefundAmount } from './formatReturnRefundAmount'

interface ReturnOrderInfoCardProps {
  returnData: ReturnRoot
}

const REFUND_RESOLVED_STATUSES: ReadonlySet<ReturnRoot['status']> = new Set([
  'APPROVED',
  'REFUNDED',
])

export function ReturnOrderInfoCard({ returnData }: ReturnOrderInfoCardProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Amount and partial flag are server-decided (서버 주도 UI 정책); never derived from items.
  const refundAmountText = formatReturnRefundAmount(t, returnData)
  const showPartialRefundTag =
    REFUND_RESOLVED_STATUSES.has(returnData.status) && returnData.isPartialRefund === true

  return (
    <Card title={t('pages.returns.sections.orderInfo')} bordered>
      <Descriptions column={1} size="small">
        <Descriptions.Item label={t('pages.returns.fields.orderId')}>
          <Button
            data-testid="return-detail-order-link"
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => void navigate(`/orders/${returnData.order.fulfillmentOrderId}`)}
          >
            {formatOrderBriefLabel(returnData.order)}
          </Button>
        </Descriptions.Item>
        {returnData.returnItems.map((item) => (
          <Descriptions.Item
            key={item.productListingId}
            label={<ProductCell name={item.productName} thumbnailUrl={item.productThumbnailUrl} />}
          >
            {`${item.quantity} x ${item.unitPrice ? formatKrw(item.unitPrice.amount) : '-'}`}
          </Descriptions.Item>
        ))}
        <Descriptions.Item label={t('pages.returns.fields.refundAmount')}>
          <Space size="small">
            {refundAmountText ?? '-'}
            {showPartialRefundTag && (
              <Tag color="gold" data-testid="return-partial-refund-tag">
                {t('pages.returns.fields.partialRefund')}
              </Tag>
            )}
          </Space>
        </Descriptions.Item>
      </Descriptions>
    </Card>
  )
}
